import React, { useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';

// Definir tipos para o state do player
export interface PlayerState {
  socketId: string;
  peerId: string;
  position: { x: number, y: number, z: number };
  rotation: { x: number, y: number, z: number };
  velocity?: { x: number, y: number, z: number };
  timestamp?: number;
  persistentId?: string;
  hasMultiversoPass?: boolean; // Indica se o jogador possui um Multiverso Pass
}

interface MultiplayerManagerProps {
  onLocalPlayerMove: (position: PlayerState['position'], rotation: PlayerState['rotation']) => void;
  onPlayersUpdate: (players: PlayerState[]) => void;
}

// Interface para os métodos expostos pelo componente
export interface MultiplayerManagerRef {
  updatePosition: (position: PlayerState['position'], rotation: PlayerState['rotation']) => void;
  updateMultiversoPassStatus: (hasAccess: boolean) => void; // Adicionado para atualizar o status do Multiverso Pass
  getLocalSocketId: () => string | undefined;
  getAllPlayers: () => PlayerState[];
  getLocalPosition: () => PlayerState['position'];
  getLocalRotation: () => PlayerState['rotation'];
  getSocket: () => Socket | null;
}

// Extend Socket to include engine transports/upgrades
type ExtendedSocket = Socket & { io: any };

const MultiplayerManager = React.forwardRef<MultiplayerManagerRef, MultiplayerManagerProps>(
  ({ onLocalPlayerMove, onPlayersUpdate }, ref): React.ReactElement | null => {
  const [players, setPlayers] = useState<Map<string, PlayerState>>(new Map());
  const socketRef = useRef<ExtendedSocket | null>(null);
  // Gerar um ID mais curto para identificar o jogador
  const generateShortId = () => {
    // Usar apenas os primeiros 8 caracteres do UUID para evitar IDs muito longos
    return `user-${uuidv4().substring(0, 8)}`;
  };

  // Obter ID persistente do localStorage ou gerar um novo
  const getOrCreatePersistentId = () => {
    try {
      const storedId = localStorage.getItem('multiverso-player-id');
      if (storedId) {
        // Remover completamente os logs de ID persistente
        return storedId;
      }

      // Se não existir, criar um novo ID e armazenar
      const newId = generateShortId();
      localStorage.setItem('multiverso-player-id', newId);
      // Manter apenas o log de criação de novo ID (evento raro)
      console.log('Novo ID persistente criado e armazenado:', newId);
      return newId;
    } catch (error) {
      // Em caso de erro (ex: localStorage bloqueado), apenas retornar um ID temporário
      console.warn('Não foi possível acessar localStorage, usando ID temporário');
      return generateShortId();
    }
  };

  const localPlayerIdRef = useRef<string>(getOrCreatePersistentId());

  useEffect(() => {
    console.log('MultiplayerManager: Inicializando...');

    // Usar o socket global em vez de criar um novo
    if (!(window as any)._globalSocket) {
      console.error('Socket global não encontrado! Isso é um erro crítico.');
      return;
    }

    // Usar o socket global
    socketRef.current = (window as any)._globalSocket;

    console.log('Socket global obtido:', socketRef.current?.id || 'não conectado');

    // Verificar se o socket está conectado
    if (socketRef.current?.connected) {
      console.log('Socket global já está conectado:', socketRef.current.id);
    } else {
      console.log('Socket global não está conectado, aguardando conexão...');
    }

    // Remover qualquer elemento de status existente no DOM
    const existingStatusElement = document.getElementById('connection-status');
    if (existingStatusElement && existingStatusElement.parentNode) {
      existingStatusElement.parentNode.removeChild(existingStatusElement);
    }

    // Adicionar um elemento oculto ao DOM com o ID do socket para referência
    const socketElement = document.createElement('div');
    socketElement.id = 'multiplayer-socket-id';
    socketElement.style.display = 'none';
    socketElement.dataset.socketId = 'pending';
    socketElement.dataset.connectionStatus = 'connecting';
    document.body.appendChild(socketElement);

    // Monitorar eventos de conexão do Socket.io
    socketRef.current.on('connect', () => {
      console.log('Socket.io conectado com sucesso:', socketRef.current?.id);
      if (socketElement && socketRef.current) {
        socketElement.dataset.socketId = socketRef.current.id || '';
        console.log('Socket ID atualizado no DOM:', socketRef.current.id);
      }

      // Flush de posição enfileirada após reconexão
      if (positionQueueRef.current) {
        const queued = positionQueueRef.current;
        sendPositionUpdate(queued.position, queued.rotation);
        positionQueueRef.current = null;
      }
    });

    // Monitorar eventos de erro do Socket.io
    socketRef.current.on('connect_error', (err: any) => {
      console.error('Socket.io erro de conexão:', err);
    });

    socketRef.current.on('connect_timeout', () => {
      console.error('Socket.io timeout de conexão');
    });

    socketRef.current.on('error', (err: any) => {
      console.error('Socket.io erro:', err);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('Socket.io desconectado:', reason);

      // Atualizar elementos de status
      if (socketElement) {
        socketElement.dataset.socketId = 'disconnected';
        socketElement.dataset.connectionStatus = 'disconnected';
      }

      // Deixar o Socket.io gerenciar a reconexão automaticamente
      console.log('Deixando o Socket.io gerenciar a reconexão automaticamente...');
    });

    // Verificar periodicamente se o jogador local está registrado (com frequência reduzida)
    const checkConnectionInterval = setInterval(() => {
      if (socketRef.current && socketRef.current.connected) {
        // Se estiver conectado, verificar se o jogador local está registrado
        const localSocketId = socketRef.current.id;
        const localPlayer = players.get(localSocketId);

        if (!localPlayer && localSocketId) {
          console.log('[MULTIPLAYER] Jogador local não encontrado, registrando...');
          // Registrar o jogador local
          const initialPosition = localPositionRef.current || { x: -14.5, y: 14.2, z: 315.0 };
          const initialRotation = localRotationRef.current || { x: 0, y: 0, z: 0 };

          try {
            socketRef.current.emit('player:join', {
              position: initialPosition,
              rotation: initialRotation,
              persistentId: localPlayerIdRef.current,
              hasMultiversoPass: hasMultiversoPassRef.current // Incluir status atual do Multiverso Pass
            });
            console.log('[MULTIPLAYER] Evento player:join enviado para registrar jogador local');
          } catch (error) {
            console.error('[MULTIPLAYER] Erro ao enviar evento player:join:', error);
          }
        } else if (localPlayer) {
          // Verificar se o status do Multiverso Pass está sincronizado
          if (localPlayer.hasMultiversoPass !== hasMultiversoPassRef.current) {
            console.log(`[MULTIPLAYER] Status do Multiverso Pass desincronizado. Local: ${hasMultiversoPassRef.current}, Servidor: ${localPlayer.hasMultiversoPass}`);

            // Atualizar o status no servidor
            try {
              socketRef.current.emit('player:update', {
                hasMultiversoPass: hasMultiversoPassRef.current
              });
              console.log(`[MULTIPLAYER] Enviada atualização de status do Multiverso Pass: ${hasMultiversoPassRef.current}`);
            } catch (error) {
              console.error('[MULTIPLAYER] Erro ao enviar atualização de status do Multiverso Pass:', error);
            }
          }
        }
      }
    }, 30 * 60 * 1000); // Verificar a cada 30 minutos (mesmo intervalo do Multiverso Pass)

    // Adicionar listeners para eventos de conexão com tratamento de erros melhorado
    socketRef.current.on('connect_error', (error) => {
      console.error('[MULTIPLAYER] Socket.io connect_error:', error);

      // Verificar se é um erro de CORS ou XHR
      if (error.message && (error.message.includes('xhr poll error') || error.message.includes('CORS'))) {
        console.log('[MULTIPLAYER] Detectado erro de XHR polling ou CORS, tentando reconectar com configurações alternativas...');

        // Tentar reconectar com configurações alternativas
        try {
          // Forçar desconexão para limpar estado
          socketRef.current?.disconnect();

          // Reconectar após um breve atraso
          setTimeout(() => {
            console.log('[MULTIPLAYER] Tentando reconectar após erro de XHR...');
            socketRef.current?.connect();
          }, 2000);
        } catch (reconnectError) {
          console.error('[MULTIPLAYER] Erro ao tentar reconectar:', reconnectError);
        }
      }
    });

    socketRef.current.on('connect_timeout', () => {
      console.error('[MULTIPLAYER] Socket.io connect_timeout');
    });

    socketRef.current.on('reconnect', (attemptNumber) => {
      console.log(`[MULTIPLAYER] Socket.io reconectado após ${attemptNumber} tentativas`);

      // Solicitar lista de jogadores após reconexão bem-sucedida
      setTimeout(() => {
        console.log('[MULTIPLAYER] Solicitando lista de jogadores após reconexão bem-sucedida');
        socketRef.current?.emit('players:get');
      }, 1000);
    });

    socketRef.current.on('reconnect_attempt', (attemptNumber) => {
      // Ajustar delay de reconexão usando backoff exponencial
      const delay = Math.min(2000 * 2 ** attemptNumber, 10000);
      if (socketRef.current && (socketRef.current as any).io && (socketRef.current as any).io.opts) {
        (socketRef.current as any).io.opts.reconnectionDelay = delay;
      }
      console.log(`[MULTIPLAYER] Tentativa de reconexão #${attemptNumber}, próximo delay ${delay}ms`);
    });

    socketRef.current.on('reconnect_error', (error) => {
      console.error('[MULTIPLAYER] Socket.io reconnect_error:', error);
    });

    socketRef.current.on('reconnect_failed', () => {
      console.error('[MULTIPLAYER] Socket.io reconnect_failed - tentativas de reconexão esgotadas');

      // Tentar uma última reconexão com configurações diferentes
      try {
        console.log('[MULTIPLAYER] Tentando reconexão final com configurações alternativas...');
        socketRef.current?.disconnect();

        // Reconectar após um breve atraso
        setTimeout(() => {
          socketRef.current?.connect();
        }, 3000);
      } catch (finalError) {
        console.error('[MULTIPLAYER] Erro na tentativa final de reconexão:', finalError);
      }
    });

    socketRef.current.on('error', (error) => {
      console.error('[MULTIPLAYER] Socket.io error:', error);
    });

    // Handler para resposta de ping (pong) do servidor
    socketRef.current.on('pong', (data) => {
      // Calcular latência
      const latency = data.received ? Date.now() - data.received : 0;

      // Limitar logs para evitar spam no console
      if (Math.random() < 0.05) {
        console.log(`Pong recebido do servidor. Latência: ${latency}ms`);
      }

      // Atualizar status de conexão se necessário
      if (latency > 1000) {
        console.warn(`Alta latência detectada: ${latency}ms`);
      }
    });

    // PeerJS foi removido, agora usamos apenas Socket.io para todas as comunicações

    // Posição inicial aleatória
    const initialPosition = {
      x: Math.random() * 40 - 20,
      y: 10,
      z: Math.random() * 40 - 20
    };

    const initialRotation = { x: 0, y: 0, z: 0 };

    // Registrar o jogador após a conexão
    socketRef.current.on('connect', () => {
      console.log('[MULTIPLAYER] Socket.io conectado com sucesso!');
      console.log('[MULTIPLAYER] Socket ID:', socketRef.current?.id);

      // Verificar se o socket tem informações de transporte
      if ((socketRef.current as any)?.io?.engine) {
        console.log('[MULTIPLAYER] Transport usado:', (socketRef.current as any).io.engine.transport.name);
        console.log('[MULTIPLAYER] Upgrades disponíveis:', (socketRef.current as any).io.engine.upgrades);
      }

      console.log('[MULTIPLAYER] Socket está confirmado como conectado');

      // Atualizar elementos de status
      if (socketElement) {
        socketElement.dataset.socketId = socketRef.current?.id || 'connected';
        socketElement.dataset.connectionStatus = 'connected';
      }

      // Processar posição enfileirada após reconexão
      if (positionQueueRef.current) {
        console.log('[MULTIPLAYER] Processando posição enfileirada após reconexão');
        const { position, rotation } = positionQueueRef.current;

        // Pequeno atraso para garantir que a conexão está estável
        setTimeout(() => {
          sendPositionUpdate(position, rotation);
        }, 500);
      }

      console.log('Enviando dados de registro do jogador...');

      // Usar a posição inicial definida
      console.log('Enviando posição inicial definida para o servidor');

      socketRef.current?.emit('player:join', {
        position: initialPosition,
        rotation: initialRotation,
        persistentId: localPlayerIdRef.current // Incluir ID persistente
      });

      console.log('Evento player:join enviado com sucesso');

      // Atualizar as referências locais
      localPositionRef.current = initialPosition;
      localRotationRef.current = initialRotation;

      // Notificar componente pai sobre posição inicial
      onLocalPlayerMove(initialPosition, initialRotation);

      // Adicionar jogador local ao estado de players para sincronização inicial
      const localPlayer: PlayerState = {
        socketId: socketRef.current!.id!,
        peerId: localPlayerIdRef.current,
        position: initialPosition,
        rotation: initialRotation,
        velocity: { x: 0, y: 0, z: 0 },
        timestamp: Date.now()
      };
      updatePlayersList(localPlayer.socketId, localPlayer);
    });

    // Ouvir por novos jogadores
    socketRef.current.on('player:new', (newPlayer: PlayerState) => {
      console.log('[MULTIPLAYER] Received player:new', newPlayer);

      // Verificar se já temos este jogador com o mesmo ID persistente
      let duplicateFound = false;

      if (newPlayer.persistentId) {
        for (const [existingId, existingPlayer] of players.entries()) {
          if (existingPlayer.persistentId === newPlayer.persistentId && existingId !== newPlayer.socketId) {
            console.log(`Ignorando jogador duplicado: ${newPlayer.socketId} (mesmo persistentId: ${newPlayer.persistentId})`);
            duplicateFound = true;
            break;
          }
        }
      }

      if (!duplicateFound) {
        // Garantir propriedades essenciais (velocity e timestamp)
        const processedPlayer: PlayerState = {
          ...newPlayer,
          velocity: newPlayer.velocity ?? { x: 0, y: 0, z: 0 },
          timestamp: newPlayer.timestamp ?? Date.now(),
          persistentId: newPlayer.persistentId || newPlayer.socketId
        };
        updatePlayersList(processedPlayer.socketId, processedPlayer);
      }
    });

    // Receber lista inicial de jogadores
    socketRef.current.on('players:list', (existingPlayers: PlayerState[]) => {
      console.log('[MULTIPLAYER] Received players:list', existingPlayers.length, existingPlayers);

      // Log detalhado para depuração do status do Multiverso Pass
      existingPlayers.forEach(player => {
        console.log(`[MULTIPLAYER] Jogador ${player.socketId} na lista com hasMultiversoPass=${player.hasMultiversoPass}`);
      });

      // Limpar o mapa de jogadores e adicionar apenas os jogadores da lista recebida
      // Isso garante que não há jogadores duplicados ou fantasmas
      setPlayers(prevPlayers => {
        // Criar um novo mapa vazio
        const newPlayersMap = new Map<string, PlayerState>();

        // Manter apenas o jogador local se existir
        const localSocketId = socketRef.current?.id;
        const localPlayer = prevPlayers.get(localSocketId!);
        if (localSocketId && localPlayer) {
          // Garantir que o jogador local mantenha seu status do Multiverso Pass
          const updatedLocalPlayer = {
            ...localPlayer,
            hasMultiversoPass: hasMultiversoPassRef.current
          };
          newPlayersMap.set(localSocketId, updatedLocalPlayer);
          console.log(`[MULTIPLAYER] Mantendo jogador local com hasMultiversoPass=${updatedLocalPlayer.hasMultiversoPass}`);
        }

        // Adicionar jogadores da lista recebida, garantindo propriedades essenciais
        existingPlayers.forEach(player => {
          // Ignorar o jogador local, já adicionado acima
          if (player.socketId !== localSocketId) {
            // Verificar se já existe um jogador com o mesmo ID persistente
            let shouldAdd = true;

            if (player.persistentId) {
              for (const [existingId, existingPlayer] of newPlayersMap.entries()) {
                if (existingPlayer.persistentId === player.persistentId && existingId !== player.socketId) {
                  console.log(`Ignorando jogador duplicado na lista: ${player.socketId} (mesmo persistentId: ${player.persistentId})`);
                  shouldAdd = false;
                  break;
                }
              }
            }

            if (shouldAdd) {
              const processedPlayer = {
                ...player,
                velocity: player.velocity ?? { x: 0, y: 0, z: 0 },
                timestamp: player.timestamp ?? Date.now(),
                persistentId: player.persistentId || player.socketId,
                hasMultiversoPass: player.hasMultiversoPass // Garantir que o status do Multiverso Pass seja mantido
              };
              newPlayersMap.set(player.socketId, processedPlayer);
              console.log(`[MULTIPLAYER] Adicionando jogador ${player.socketId} com hasMultiversoPass=${processedPlayer.hasMultiversoPass}`);
            }
          }
        });

        // Notificar componente pai com lista atualizada
        const playersArray = Array.from(newPlayersMap.values());
        onPlayersUpdate(playersArray);

        return newPlayersMap;
      });
    });

    // Atualizar posição de jogador
    socketRef.current.on('player:moved', (update: {
      socketId: string;
      position: PlayerState['position'];
      rotation: PlayerState['rotation'];
      velocity?: PlayerState['velocity'];
      timestamp?: number;
      persistentId?: string;
      hasMultiversoPass?: boolean; // Adicionado para receber o status do Multiverso Pass
    }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[MULTIPLAYER] Received player:moved', update);
      }
      const now = Date.now();

      // Verificar se o jogador já existe no mapa
      const player = players.get(update.socketId);

      if (player) {
        // Atualizar jogador existente
        const updatedPlayer = {
          ...player,
          position: update.position,
          rotation: update.rotation,
          velocity: update.velocity || { x: 0, y: 0, z: 0 },
          timestamp: update.timestamp || now,
          persistentId: update.persistentId || player.persistentId,
          // Manter o status do Multiverso Pass se não for fornecido na atualização
          hasMultiversoPass: update.hasMultiversoPass !== undefined ? update.hasMultiversoPass : player.hasMultiversoPass
        };

        // Atualizar o jogador no mapa
        updatePlayersList(update.socketId, updatedPlayer);

        // Log detalhado para debug do status do Multiverso Pass
        if (update.hasMultiversoPass !== undefined) {
          console.log(`[MULTIPLAYER] Jogador ${update.socketId} atualizou hasMultiversoPass=${update.hasMultiversoPass} via player:moved`);
        }
      } else {
        // Se o jogador não existe no nosso mapa, vamos criá-lo
        if (process.env.NODE_ENV === 'development') {
          console.log(`Adding new player from update: ${update.socketId}`, update.position);
        }

        // Criar novo jogador com os dados recebidos
        const newPlayer: PlayerState = {
          socketId: update.socketId,
          peerId: update.persistentId || update.socketId, // Usar persistentId se disponível
          position: update.position,
          rotation: update.rotation,
          velocity: update.velocity || { x: 0, y: 0, z: 0 },
          timestamp: update.timestamp || now,
          persistentId: update.persistentId || update.socketId,
          hasMultiversoPass: update.hasMultiversoPass // Incluir status do Multiverso Pass se fornecido
        };

        // Adicionar ao mapa de jogadores e atualizar a lista
        updatePlayersList(update.socketId, newPlayer);

        // Log detalhado para debug do status do Multiverso Pass - apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.log(`[MULTIPLAYER] Novo jogador ${update.socketId} adicionado com hasMultiversoPass=${update.hasMultiversoPass}`);
        }
      }
    });

    // Atualizar informações do jogador (como status do Multiverso Pass)
    socketRef.current.on('player:updated', (update: {
      socketId: string;
      hasMultiversoPass?: boolean;
      timestamp?: number;
      persistentId?: string;
    }) => {
      console.log('[MULTIPLAYER] Received player:updated', update);

      // Verificar se o jogador existe no mapa
      const player = players.get(update.socketId);

      if (player) {
        // Atualizar jogador existente
        const updatedPlayer = {
          ...player,
          hasMultiversoPass: update.hasMultiversoPass !== undefined ? update.hasMultiversoPass : player.hasMultiversoPass,
          timestamp: update.timestamp || Date.now(),
          persistentId: update.persistentId || player.persistentId
        };

        // Atualizar o jogador no mapa
        updatePlayersList(update.socketId, updatedPlayer);

        // Log detalhado para debug
        console.log(`[MULTIPLAYER] Jogador ${update.socketId} atualizado com hasMultiversoPass=${updatedPlayer.hasMultiversoPass}`);

        // Forçar uma atualização da posição para garantir que o modelo correto seja exibido
        // Isso é importante para garantir que todos os clientes vejam o modelo correto
        if (update.socketId === socketRef.current?.id && localPositionRef.current && localRotationRef.current) {
          console.log(`[MULTIPLAYER] Forçando atualização de posição após player:updated para jogador local`);
          setTimeout(() => {
            sendPositionUpdate(localPositionRef.current, localRotationRef.current);
          }, 100);
        }
      } else {
        // Se o jogador não existe no nosso mapa, vamos solicitar a lista completa
        console.log(`[MULTIPLAYER] Jogador ${update.socketId} não encontrado no mapa local. Solicitando lista atualizada.`);
        socketRef.current?.emit('players:get');

        // Se for uma atualização para o jogador local, criar um novo jogador
        if (update.socketId === socketRef.current?.id) {
          console.log(`[MULTIPLAYER] Criando jogador local após player:updated`);
          const newPlayer: PlayerState = {
            socketId: update.socketId,
            peerId: update.persistentId || update.socketId,
            position: localPositionRef.current || { x: 0, y: 0, z: 0 },
            rotation: localRotationRef.current || { x: 0, y: 0, z: 0 },
            velocity: { x: 0, y: 0, z: 0 },
            timestamp: update.timestamp || Date.now(),
            persistentId: update.persistentId || update.socketId,
            hasMultiversoPass: update.hasMultiversoPass
          };

          // Atualizar o jogador no mapa
          updatePlayersList(update.socketId, newPlayer);

          // Forçar uma atualização da posição
          if (localPositionRef.current && localRotationRef.current) {
            console.log(`[MULTIPLAYER] Forçando atualização de posição após criar jogador local`);
            setTimeout(() => {
              sendPositionUpdate(localPositionRef.current, localRotationRef.current);
            }, 100);
          }
        }
      }
    });

    // Remover jogador desconectado
    socketRef.current.on('player:left', (data: { socketId: string }) => {
      console.log('[MULTIPLAYER] Received player:left', data.socketId);
      const newPlayers = new Map(players);
      newPlayers.delete(data.socketId);
      setPlayers(newPlayers);
      onPlayersUpdate(Array.from(newPlayers.values()));
    });

    // Conectar manualmente após registrar todos os eventos
    // socketRef.current?.connect();

    // Solicitar lista completa de jogadores após um pequeno atraso
    setTimeout(() => {
      console.log('[MULTIPLAYER] Solicitando lista completa de jogadores após inicialização');
      socketRef.current?.emit('players:get');
    }, 2000);

    return () => {
      // Limpar intervalo de verificação de conexão
      if (checkConnectionInterval) {
        clearInterval(checkConnectionInterval);
      }

      // Remover todos os listeners antes de desconectar
      if (socketRef.current) {
        console.log('Removendo listeners do socket antes de desconectar');
        socketRef.current.off('connect');
        socketRef.current.off('disconnect');
        socketRef.current.off('connect_error');
        socketRef.current.off('connect_timeout');
        socketRef.current.off('error');
        socketRef.current.off('reconnect');
        socketRef.current.off('reconnect_attempt');
        socketRef.current.off('reconnect_error');
        socketRef.current.off('reconnect_failed');
        socketRef.current.off('pong');
        socketRef.current.off('player:new');
        socketRef.current.off('players:list');
        socketRef.current.off('player:moved');
        socketRef.current.off('player:updated');
        socketRef.current.off('player:left');

        // Desconectar o socket
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Função auxiliar para atualizar lista de jogadores
  const updatePlayersList = (socketId: string, playerData: PlayerState) => {
    // Verificar se o jogador já existe com o mesmo ID persistente
    setPlayers(prevPlayers => {
      const newPlayers = new Map(prevPlayers);

      // Remover qualquer jogador existente com o mesmo ID persistente
      if (playerData.persistentId) {
        for (const [existingId, existingPlayer] of prevPlayers.entries()) {
          if (existingPlayer.persistentId === playerData.persistentId && existingId !== socketId) {
            console.log(`Removendo jogador duplicado localmente: ${existingId} (mesmo persistentId: ${playerData.persistentId})`);
            newPlayers.delete(existingId);
          }
        }
      }

      // Adicionar ou atualizar o jogador
      newPlayers.set(socketId, playerData);

      // Notificar componente pai
      const playersArray = Array.from(newPlayers.values());
      onPlayersUpdate(playersArray);

      return newPlayers;
    });
  };

  // Estado para controle de frequência de envio
  const lastPositionUpdateRef = useRef<number>(0);
  const positionQueueRef = useRef<{position: PlayerState['position'], rotation: PlayerState['rotation']} | null>(null);

  // Referência para armazenar o status do Multiverso Pass
  const hasMultiversoPassRef = useRef<boolean>(false);

  // Função simplificada para enviar posição
  const sendPositionUpdate = (position: PlayerState['position'], rotation: PlayerState['rotation']) => {
    // Sempre armazenar a posição atual nas referências locais
    localPositionRef.current = position;
    localRotationRef.current = rotation;

    // Se o socket não estiver conectado, armazenar a posição atual e tentar reconectar
    if (!socketRef.current || !socketRef.current.connected) {
      console.log('[MULTIPLAYER] Socket não conectado, armazenando posição atual');

      // Armazenar a posição atual para enviar quando reconectar
      positionQueueRef.current = { position, rotation };

      // Tentar reconectar se não estiver em processo de reconexão
      if (socketRef.current && !socketRef.current.connecting) {
        console.log('[MULTIPLAYER] Tentando reconectar automaticamente...');
        try {
          socketRef.current.connect();
        } catch (error) {
          console.error('[MULTIPLAYER] Erro ao tentar reconectar:', error);
        }
      }

      return;
    }

    const now = Date.now();
    // Usar um intervalo de 100ms (10fps) para melhorar a fluidez
    const UPDATE_INTERVAL = 100;

    // Atualizar o jogador local no mapa de jogadores
    if (socketRef.current?.id) {
      const localPlayer: PlayerState = {
        socketId: socketRef.current.id,
        peerId: localPlayerIdRef.current,
        position,
        rotation,
        velocity: { x: 0, y: 0, z: 0 }, // Simplificar velocidade
        timestamp: now,
        persistentId: localPlayerIdRef.current,
        hasMultiversoPass: hasMultiversoPassRef.current // Incluir status do Multiverso Pass
      };

      // Atualizar o jogador local no mapa
      updatePlayersList(socketRef.current.id, localPlayer);

      // Notificar componente pai sobre a mudança de posição
      onLocalPlayerMove(position, rotation);
    }

    // Verificar se é hora de enviar uma atualização para o servidor
    if (now - lastPositionUpdateRef.current > UPDATE_INTERVAL) {
      try {
        // Enviar atualização para o servidor incluindo o status do Multiverso Pass
        socketRef.current.emit('player:move', {
          position,
          rotation,
          timestamp: now,
          persistentId: localPlayerIdRef.current,
          hasMultiversoPass: hasMultiversoPassRef.current // Incluir status do Multiverso Pass
        });

        // Atualizar timestamp da última atualização
        lastPositionUpdateRef.current = now;

        // Limpar a posição enfileirada, pois conseguimos enviar com sucesso
        positionQueueRef.current = null;
      } catch (error) {
        console.error('[MULTIPLAYER] Erro ao enviar atualização de posição:', error);

        // Armazenar a posição atual para tentar enviar mais tarde
        positionQueueRef.current = { position, rotation };

        // Verificar se o socket ainda está conectado
        if (socketRef.current && !socketRef.current.connected) {
          console.log('[MULTIPLAYER] Socket desconectado durante envio, tentando reconectar...');
          try {
            socketRef.current.connect();
          } catch (reconnectError) {
            console.error('[MULTIPLAYER] Erro ao tentar reconectar após falha de envio:', reconnectError);
          }
        }
      }
    }
  };

  // Enviar uma atualização periódica para manter a sincronização
  useEffect(() => {
    // Enviar uma atualização a cada 3 segundos para manter a sincronização
    const interval = setInterval(() => {
      const now = Date.now();

      // Apenas enviar se o socket estiver conectado e tivermos um ID
      if (socketRef.current?.connected && socketRef.current?.id) {
        // Usar a posição atual das referências locais
        const position = localPositionRef.current;
        const rotation = localRotationRef.current;

        if (position && rotation) {
          try {
            socketRef.current.emit('player:move', {
              position,
              rotation,
              timestamp: now,
              persistentId: localPlayerIdRef.current,
              hasMultiversoPass: hasMultiversoPassRef.current // Incluir status do Multiverso Pass
            });
            if (process.env.NODE_ENV === 'development') {
              console.log('Enviando atualização periódica de posição');
            }
            lastPositionUpdateRef.current = now;
          } catch (error) {
            console.error('Erro ao enviar atualização periódica:', error);
          }
        }
      }
    }, 3000); // Verificar a cada 3 segundos

    return () => clearInterval(interval);
  }, []);

  // Referências para a posição e rotação local
  const localPositionRef = useRef<PlayerState['position']>({ x: 0, y: 0, z: 0 });
  const localRotationRef = useRef<PlayerState['rotation']>({ x: 0, y: 0, z: 0 });

  // Funções expostas para o componente pai
  React.useImperativeHandle(ref, () => ({
    // Atualizar posição da nave
    updatePosition: (position: PlayerState['position'], rotation: PlayerState['rotation']) => {
      // Atualizar as referências locais
      localPositionRef.current = position;
      localRotationRef.current = rotation;
      sendPositionUpdate(position, rotation);
    },
    // Atualizar status do Multiverso Pass
    updateMultiversoPassStatus: (hasAccess: boolean) => {
      console.log(`[MULTIPLAYER] Atualizando status do Multiverso Pass: ${hasAccess}`);

      // Verificar se o status mudou para evitar atualizações desnecessárias
      if (hasMultiversoPassRef.current === hasAccess) {
        console.log(`[MULTIPLAYER] Status do Multiverso Pass já está como ${hasAccess}, ignorando atualização`);
        return;
      }

      // Atualizar a referência local
      hasMultiversoPassRef.current = hasAccess;

      // Atualizar o jogador local no mapa de jogadores
      if (socketRef.current?.id) {
        const localPlayer = players.get(socketRef.current.id);
        if (localPlayer) {
          const updatedPlayer = {
            ...localPlayer,
            hasMultiversoPass: hasAccess
          };
          updatePlayersList(socketRef.current.id, updatedPlayer);
          console.log(`[MULTIPLAYER] Jogador local atualizado com hasMultiversoPass=${hasAccess}`);
        }
      }

      // Enviar atualização para o servidor
      if (socketRef.current && socketRef.current.connected) {
        console.log(`[MULTIPLAYER] Enviando player:update com hasMultiversoPass=${hasAccess}`);
        socketRef.current.emit('player:update', {
          hasMultiversoPass: hasAccess,
          timestamp: Date.now(),
          persistentId: localPlayerIdRef.current
        });

        // Forçar uma única atualização de posição para garantir que todos recebam o status atualizado
        if (localPositionRef.current && localRotationRef.current) {
          console.log(`[MULTIPLAYER] Forçando atualização de posição após mudança de status do Multiverso Pass`);

          // Pequeno atraso para garantir que o servidor processou a atualização de status
          setTimeout(() => {
            sendPositionUpdate(localPositionRef.current, localRotationRef.current);
          }, 500);
        }
      }
    },
    // Obter o ID do socket local
    getLocalSocketId: () => {
      return socketRef.current?.id;
    },
    // Obter todos os jogadores
    getAllPlayers: () => {
      return Array.from(players.values());
    },
    // Obter a posição local
    getLocalPosition: () => {
      return localPositionRef.current;
    },
    // Obter a rotação local
    getLocalRotation: () => {
      return localRotationRef.current;
    },
    // Obter o socket para uso no chat
    getSocket: () => {
      return socketRef.current;
    }
  }));

  // Este componente não renderiza nada no contexto 3D
  return null;
});

export default MultiplayerManager;
